import { isPaidCourse } from '../utils/priceUtils';

const STORAGE_KEY = 'infobloom_enrollments_v1';

function loadAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveAll(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore quota errors
  }
}

export function getUserEnrollments(userId) {
  if (!userId) return [];
  const all = loadAll();
  return all[userId] || [];
}

export function enrollCourses(userId, courses) {
  if (!userId) return [];
  const all = loadAll();
  const current = all[userId] || [];
  const map = new Map(current.map((c) => [String(c.id), c]));

  const list = Array.isArray(courses) ? courses : [courses];
  list.forEach((c) => {
    if (!c || c.id == null) return;
    const id = String(c.id);
    const existing = map.get(id) || {};
    const instructorName =
      typeof c.instructor === 'object'
        ? c.instructor?.name || existing.instructor || 'Instructor'
        : c.instructor || existing.instructor || 'Instructor';
    const normalized = {
      id,
      title: c.title || existing.title || 'Course',
      thumbnail: c.thumbnail || c.banner || existing.thumbnail || '',
      banner: c.banner || existing.banner || '',
      level: c.level || existing.level || '',
      duration: c.duration || existing.duration || '',
      instructor: instructorName,
      price: c.price ?? existing.price ?? 'Free',
      progress: c.progress ?? existing.progress ?? 0,
    };
    map.set(id, normalized);
  });

  const updated = Array.from(map.values());
  all[userId] = updated;
  saveAll(all);
  return updated;
}

export function isCourseEnrolled(userId, courseId) {
  if (!userId || !courseId) return false;
  const enrolled = getUserEnrollments(userId);
  return enrolled.some((c) => String(c.id) === String(courseId));
}

/**
 * Merge Firestore enrollments (admin-activated) with local enrollments, and merge in progress from Firestore.
 * Returns Promise<array> for use in My Learning, Profile, etc.
 */
export async function getMergedEnrollments(userId) {
  if (!userId) return [];
  const { getEnrollmentsForUser } = await import('../services/firebaseEnrollments');
  const { getAllProgressForUser } = await import('../services/firebaseProgress');
  const [firestoreList, localList, progressMap] = await Promise.all([
    getEnrollmentsForUser(userId).catch(() => []),
    Promise.resolve(getUserEnrollments(userId)),
    getAllProgressForUser(userId).catch(() => new Map()),
  ]);
  const merged = [...firestoreList];
  const firestoreIds = new Set(firestoreList.map((c) => String(c.id)));
  localList.forEach((c) => {
    if (!c || c.id == null) return;
    const id = String(c.id);
    if (firestoreIds.has(id)) return;
    // Only treat local enrollments as valid for free courses.
    if (isPaidCourse(c.price)) return;
    merged.push(c);
  });
  merged.forEach((c) => {
    const prog = progressMap.get(String(c.id));
    c.progress = prog ? prog.progressPercent : (c.progress ?? 0);
    c.enrolledAt = true;
  });
  return merged;
}

